import React, { useState, useContext, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { SurveyContext } from "../context/surveyContext";
import { SessionContext } from "../context/sessionContext";
import { planMapping } from "../assets/data/pricingData";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const CreateOrg = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { surveyData, audienceData, surveyMeta } = useContext(SurveyContext);
  const { session, setSession } = useContext(SessionContext);

  const [fullName, setFullName] = useState("");
  const [orgName, setOrgName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  const isEmailSignup = location.state?.isEmailSignup || false;
  const socialToken = location.state?.socialToken || null;
  const signupData = location.state?.signupData || null;

  let inviteData = location.state?.inviteData || null;
  if (!inviteData) {
    const storedInviteData = localStorage.getItem("pendingInviteData");
    if (storedInviteData) {
      try {
        inviteData = JSON.parse(storedInviteData);
      } catch (e) {
        console.error("Failed to parse stored invite data", e);
      }
    }
  }
  const isSuperAdminInvite =
    inviteData?.plan === "Freemium" || inviteData?.plan === "Custom";

  const isInvitedUser = !!(
    inviteData &&
    (inviteData.org_name || inviteData.invitedUserId || isSuperAdminInvite)
  );

  useEffect(() => {
    if (inviteData && !isSuperAdminInvite && !inviteData.org_name) {
      setError("Invalid invitation. Organization information is missing.");
    }
  }, [inviteData, isSuperAdminInvite]);
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };

    const handlePopState = (e) => {
      e.preventDefault();
      setShowWarningModal(true);
      window.history.pushState(null, "", window.location.href);
    };
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  useEffect(() => {
    if (location.state?.userName) {
      setFullName(location.state.userName);
    } else if (session?.user?.first_name || session?.user?.name) {
      const name = session.user.first_name || session.user.name;
      setFullName(name);
    } else {
      const token = socialToken || Cookies.get("token");
      if (token) {
        try {
          const decoded = jwtDecode(token);
          const firstName =
            decoded?.user?.first_name || decoded?.first_name || "";
          if (firstName) {
            setFullName(firstName);
          }
        } catch (e) {
          console.error("Failed to decode token:", e);
        }
      }
    }

    if (isInvitedUser && !isSuperAdminInvite && inviteData.org_name) {
      setOrgName(inviteData.org_name);
    }
  }, [location.state, isInvitedUser, inviteData, socialToken, session]);

  const isFormValid = () => fullName.trim() && orgName.trim();

  const handleLogoClick = (e) => {
    e.preventDefault();
    setPendingNavigation("/");
    setShowWarningModal(true);
  };

  const handleWarningConfirm = () => {
    setShowWarningModal(false);
    if (pendingNavigation) {
      sessionStorage.removeItem("pendingSignupFlow");
      localStorage.removeItem("pendingInviteData");
      Cookies.remove("token");
      setSession({ token: null, user: null });

      navigate(pendingNavigation);
    }
  };

  const handleContinue = async () => {
    if (!isFormValid()) return;

    setLoading(true);
    setError("");

    try {
      let token = null;
      let userEmail = "";
      let userId = null;

      if (isEmailSignup && signupData && !Cookies.get("token")) {
        const signupPayload = {
          first_name: fullName.trim(),
          email: signupData.email,
          password: signupData.password,
          role: signupData.role || inviteData?.role || "admin",
          invitedUserId:
            signupData.invitedUserId || inviteData?.invitedUserId || null,
        };

        const signupRes = await fetch(
          `${process.env.REACT_APP_API_URL}/auth/signup`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(signupPayload),
          },
        );

        const signupDataRes = await signupRes.json();
        if (!signupRes.ok || !signupDataRes.token) {
          setError(signupDataRes.message || "Signup failed.");
          setLoading(false);
          return;
        }

        token = signupDataRes.token;
        Cookies.set("token", token, { expires: 2 });
        userEmail = signupData.email;

        const verifyRes = await fetch(
          `${process.env.REACT_APP_API_URL}/auth/verifyfirsttime`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token }),
          },
        );

        const verifyData = await verifyRes.json();
        if (!verifyRes.ok || !verifyData.user) {
          setError(verifyData.message || "Verification failed.");
          setLoading(false);
          return;
        }

        userId = verifyData.user._id;
      } else {
        if (socialToken) {
          token = socialToken;
          Cookies.set("token", token, { expires: 2 });

          const verifyRes = await fetch(
            `${process.env.REACT_APP_API_URL}/auth/verifyfirsttime`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ token }),
            },
          );

          const verifyData = await verifyRes.json();

          if (!verifyRes.ok || !verifyData.user) {
            setError(verifyData.message || "Could not verify social login.");
            setLoading(false);
            return;
          }

          userId = verifyData.user._id;
          userEmail = verifyData.user.email;
        } else {
          token = Cookies.get("token");
          if (!token) {
            setError("Session expired. Please try again.");
            setLoading(false);
            return;
          }
        }

        if (inviteData?.invitedUserId) {
          userId = inviteData.invitedUserId;
        }
      }
      const rawSelectedPlan = localStorage.getItem("selectedPlan");
      const selectedPlanData = rawSelectedPlan
        ? JSON.parse(rawSelectedPlan)
        : null;

      let planName = isInvitedUser
        ? inviteData?.plan || null
        : selectedPlanData?.planName || null;

      let invitePlans = null;
      if (isSuperAdminInvite) {
        // The Freemium/Custom invite JWT (created by sendFreemiumInvite /
        // sendCustomInvite) is the only token that actually carries the
        // `plans` claim. By this point in the flow `token` has usually been
        // replaced by a plans-less session token (issued by /auth/signup,
        // /auth/google-code, or /auth/linkedin), so prefer the original
        // invite token captured from the sign-up link when we have it.
        const tokenToDecode = inviteData?.token || token;
        if (tokenToDecode) {
          try {
            const decoded = jwtDecode(tokenToDecode);
            if (decoded?.plans) invitePlans = decoded.plans;
          } catch (e) {
            console.error("Failed to decode invite token for plan details:", e);
          }
        }
      }

      let planDetails = null;
      if (invitePlans) {
        planDetails = {
          price: planName === "Freemium" ? "FREE" : null,
          credits_score: invitePlans.credits_score,
          project_limits: invitePlans.project_limits,
          question_count: invitePlans.total_question_count,
        };
      } else if (planName && planMapping[planName]) {
        planDetails = planMapping[planName];
      } else {
        planName = null;
        planDetails = null;
      }

      const plansObject = invitePlans
        ? {
            plan_name: invitePlans.plan_name || planName,
            project_limits: invitePlans.project_limits,
            credits_score: invitePlans.credits_score,
            total_project_limits:
              invitePlans.total_project_limits ?? invitePlans.project_limits,
            total_credits_score:
              invitePlans.total_credits_score ?? invitePlans.credits_score,
            // NOTE: must match the field name addUserWithOrg reads
            // (`plans.total_question_count`) — this used to be sent as
            // `question_count`, which meant it was silently ignored and the
            // configured avatar-question limit never made it onto the org.
            total_question_count: invitePlans.total_question_count,
          }
        : planDetails
          ? {
              plan_name: planName,
              project_limits: planDetails.project_limits,
              credits_score: planDetails.credits_score,
              total_project_limits: planDetails.project_limits,
              total_credits_score: planDetails.credits_score,
              total_question_count: planDetails.question_count,
            }
          : undefined;

      const addUserToOrgPayload = {
        first_name: fullName.trim(),
        org_name: orgName.trim(),
        ...(plansObject ? { plans: plansObject } : {}),
        user_id:
          inviteData?.invitedUserId !== undefined
            ? inviteData?.invitedUserId
            : userId || session?.user?.user_id || session?.user?.id,
        role: isSuperAdminInvite
          ? "admin"
          : isInvitedUser
            ? inviteData?.role
            : signupData?.role || "admin",
      };

      const addUserRes = await fetch(
        `${process.env.REACT_APP_API_URL}/auth/addUserWithOrg`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token: token,
          },
          body: JSON.stringify(addUserToOrgPayload),
        },
      );

      const addUserData = await addUserRes.json();
      if (!addUserRes.ok) {
        throw new Error(addUserData.message || "Failed to add user to org");
      }
      if (addUserData.token) {
        token = addUserData.token;
        Cookies.set("token", token, { expires: 2 });
      }

      setSession({
        token,
        user: {
          email: userEmail,
          name: fullName,
          id: userId,
          user_id: userId,
        },
      });
      if (isInvitedUser) {
        const resolvedUserId =
          inviteData?.invitedUserId ||
          userId ||
          session?.user?.user_id ||
          session?.user?.id;
        if (addUserData.paymentRequired) {
          localStorage.setItem(
            "paymentContext",
            JSON.stringify({ source: "CUSTOM_INVITE" }),
          );

          const paymentResponse = await fetch(
            `${process.env.REACT_APP_API_URL}/payment/create-checkout-session`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                token: token,
              },
              credentials: "include",
              body: JSON.stringify({
                purpose: "custom_account_activation",
                userId: resolvedUserId,
              }),
            },
          );

          const paymentData = await paymentResponse.json();
          if (!paymentResponse.ok || !paymentData.url) {
            throw new Error(
              paymentData.error ||
                "Failed to start payment for your custom account",
            );
          }

          localStorage.removeItem("userSession");
          localStorage.removeItem("selectedPlan");
          localStorage.removeItem("pendingProjectData");
          localStorage.removeItem("pendingInviteData");
          sessionStorage.removeItem("pendingSignupFlow");

          window.location.href = paymentData.url;
          return;
        }

        localStorage.removeItem("userSession");
        localStorage.removeItem("selectedPlan");
        localStorage.removeItem("pendingProjectData");
        localStorage.removeItem("pendingInviteData");
        sessionStorage.removeItem("pendingSignupFlow");

        await new Promise((r) => setTimeout(r, 100));
        window.location.href = "/projects";
        return;
      }

      if (!planName) {
        localStorage.removeItem("selectedPlan");
        sessionStorage.removeItem("pendingSignupFlow");

        await new Promise((r) => setTimeout(r, 100));
        window.location.href = "/projects";
        return;
      }
      const projectMetadata = {
        surveyFileName: surveyData?.name || null,
        surveyFileType: surveyData?.type || null,
        audienceData: audienceData || [],
        surveyMeta: surveyMeta || null,
        timestamp: new Date().toISOString(),
      };

      sessionStorage.setItem(
        "pendingProjectMetadata",
        JSON.stringify(projectMetadata),
      );
      if (
        planName === "Freemium" ||
        planName === "Custom" ||
        planDetails?.price === "FREE" ||
        planDetails?.price === 0
      ) {
        localStorage.removeItem("selectedPlan");
        localStorage.removeItem("pendingInviteData");
        sessionStorage.removeItem("pendingSignupFlow");
        await new Promise((r) => setTimeout(r, 100));
        window.location.href = "/projects";
        return;
      }

      const amount = selectedPlanData?.amount || planDetails?.price;
      if (!amount) throw new Error("Failed to determine amount for payment");

      const paymentResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/payment/create-checkout-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token: token,
          },
          credentials: "include",
          body: JSON.stringify({
            planName,
            amount,
            userId: userId || session?.user?.user_id || session?.user?.id,
          }),
        },
      );

      const paymentData = await paymentResponse.json();

      if (!paymentResponse.ok || !paymentData.url) {
        throw new Error("Failed to create payment session");
      }

      sessionStorage.removeItem("pendingSignupFlow");
      window.location.href = paymentData.url;
    } catch (error) {
      console.error("Error during org creation:", error);
      setError(error.message || "Something went wrong.");
      setLoading(false);
    }
  };

  return (
    <div className="page-bg">
      <div className="create-survey-logo">
        <Link to="/" onClick={handleLogoClick}>
          <img
            src="/json-media/img/logo.svg"
            alt="GI"
            width={50}
            style={{ padding: 5 }}
          />
        </Link>
      </div>

      <div className="content-center">
        <div className="define-audience__container">
          <div className="define-audience__content">
            <h1 className="define-audience__title">
              {isInvitedUser && !isSuperAdminInvite
                ? "Complete Your Profile"
                : "Create Organization"}
            </h1>
            <p
              style={{
                textAlign: "center",
                color: "#9ca3af",
                marginBottom: "2rem",
              }}
            >
              {isInvitedUser && !isSuperAdminInvite
                ? `You're joining ${
                    orgName || inviteData?.org_name || "the organization"
                  }`
                : "Set up your organization to get started"}
            </p>
            <div className="define-audience__field">
              <label className="define-audience__label">Your Full Name</label>
              <input
                type="text"
                className="form-text-input"
                placeholder="Enter your full name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </div>
            <div className="define-audience__field">
              <label className="define-audience__label">
                Organization Name
              </label>
              <input
                type="text"
                className="form-text-input"
                placeholder="Enter the name of your org"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                disabled={isInvitedUser && !isSuperAdminInvite}
                style={
                  isInvitedUser && !isSuperAdminInvite
                    ? {
                        opacity: 0.7,
                        cursor: "not-allowed",
                        backgroundColor: "rgba(255, 255, 255, 0.05)",
                      }
                    : {}
                }
              />
              {isInvitedUser && !isSuperAdminInvite && (
                <p
                  style={{
                    color: "#3b82f6",
                    fontSize: "0.85rem",
                    marginTop: "0.5rem",
                  }}
                >
                  Organization is pre-filled from your invitation
                </p>
              )}
            </div>
            {error && (
              <p
                className="error-text"
                style={{ textAlign: "center", marginTop: "1rem" }}
              >
                {error}
              </p>
            )}
            <div className="define-audience__actions">
              <button
                className={`define-audience__continue-button ${
                  !isFormValid() || loading
                    ? "define-audience__continue-button--disabled"
                    : ""
                }`}
                disabled={!isFormValid() || loading}
                onClick={handleContinue}
              >
                {loading
                  ? "Processing..."
                  : isInvitedUser
                    ? "Complete Setup"
                    : "Continue"}
              </button>
            </div>
          </div>
        </div>
        {/* Warning Modal */}
        <Dialog
          open={showWarningModal}
          onClose={() => setShowWarningModal(false)}
          sx={{
            "& .MuiBackdrop-root": {
              backgroundColor: "rgba(0, 0, 0, 0.8)",
            },
          }}
          PaperProps={{
            sx: {
              backgroundColor: "#000000",
              border: "2px solid #3b82f6",
              borderRadius: "8px",
              color: "white",
              width: "500px",
              maxWidth: "90vw",
              minHeight: "auto",
              position: "relative",
            },
          }}
        >
          <DialogTitle
            sx={{
              backgroundColor: "#000000",
              color: "white",
              textAlign: "center",
              fontSize: "1.3rem",
              fontWeight: "bold",
              paddingBottom: "20px",
              marginBottom: "10px",
              position: "relative",
            }}
          >
            Onboarding Incomplete
            <IconButton
              aria-label="close"
              onClick={() => setShowWarningModal(false)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: "#3b82f6",
                "&:hover": {
                  backgroundColor: "rgba(59, 130, 246, 0.1)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{
              backgroundColor: "#000000",
              color: "white",
              textAlign: "center",
              padding: "15px 20px",
              fontSize: "1rem",
            }}
          >
            <div>
              Your account has been created but onboarding is incomplete.
            </div>
            <div style={{ marginTop: "12px" }}>
              If you leave now, you can log in later and complete the
              organization setup to continue.
            </div>
          </DialogContent>
          <DialogActions
            sx={{
              backgroundColor: "#000000",
              justifyContent: "center",
              paddingBottom: "15px",
              gap: "12px",
            }}
          >
            <Button
              onClick={() => setShowWarningModal(false)}
              sx={{
                color: "#ffffff",
                border: "1px solid #ffffff",
                padding: "10px 26px",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.1)",
                  borderColor: "#666",
                },
              }}
            >
              Stay and Complete
            </Button>
            <Button
              variant="contained"
              onClick={handleWarningConfirm}
              sx={{
                backgroundColor: "#3b82f6",
                color: "white",
                padding: "10px 26px",
                "&:hover": {
                  backgroundColor: "#2563eb",
                },
              }}
            >
              Leave Anyway
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default CreateOrg;
